import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Volume2,
  Mic,
  Info,
  CheckCircle2,
  AlertTriangle,
  PlayCircle,
  ArrowLeft,
  Loader2,
  VolumeX,
  MicOff,
  Lock,
} from 'lucide-react';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { Button } from '../components/shared/Button';
import { EmailVerificationPanel } from '../components/shared/EmailVerificationPanel';
import { examService, type Exam } from '../services/exam.service';
import { sectionUsesStartExam, getExamPath } from '../config/examSections';
import type { Section } from '../config/examSections';
import { useAuth } from '../contexts/AuthContext';

type TestStatus = 'idle' | 'running' | 'success' | 'error';

export const PreExamSetupPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const section = searchParams.get('section') || 'Lezen';
  const examId = searchParams.get('examId') || null;

  const [exam, setExam] = useState<Exam | null>(null);
  const duration = exam ? `${exam.duration} min` : '';
  const [soundStatus, setSoundStatus] = useState<TestStatus>('idle');
  const [micStatus, setMicStatus] = useState<TestStatus>('idle');
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [startLoading, setStartLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [verificationRequired, setVerificationRequired] = useState(false);
  
  // Auth is enforced by ProtectedRoute

  // Load exam when examId is present (to check subscription requirement)
  useEffect(() => {
    if (!examId) {
      setExam(null);
      return;
    }
    let cancelled = false;
    examService
      .getExamById(examId)
      .then((data) => {
        if (!cancelled) setExam(data);
      })
      .catch(() => {
        if (!cancelled) setExam(null);
      });
    return () => {
      cancelled = true;
    };
  }, [examId]);

  const requiresSubscription = exam?.requiresSubscription !== false;
  const hasActiveSubscription = user?.subscription?.status === 'active';
  const hasActivePass = user?.passActive === true;
  const hasAccess = hasActiveSubscription || hasActivePass;
  const userPlan = user?.subscription?.plan;
  const allowedPlans = exam?.allowedPlans ?? [];
  const planAllowed =
    allowedPlans.length === 0 ||
    hasActivePass ||
    (userPlan != null && allowedPlans.includes(userPlan));
  const subscriptionBlocked =
    examId != null &&
    sectionUsesStartExam(section as Section) &&
    requiresSubscription &&
    (!hasAccess || !planAllowed);
  const emailVerificationBlocked =
    user?.emailVerified === false && hasAccess;

  // Check if section requires microphone (only Spreken)
  const needsMic = section === 'Spreken';
  // Reading and writing exams don't need sound or mic checks
  const needsSound = section !== 'Lezen' && section !== 'Schrijven' && section !== 'KNM';
  
  const allReady =
    (!needsSound || soundStatus === 'success') &&
    (!needsMic || micStatus === 'success') &&
    acceptedRules;

  const handleSoundTest = () => {
    setSoundStatus('running');
    
    // Create a simple beep sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 440; // A4 note
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
        setSoundStatus('success');
        if (currentStep === 0) setCurrentStep(1);
      }, 1000);
    } catch (error) {
      console.error('Sound test failed:', error);
      // Fallback: just simulate success after delay
      setTimeout(() => {
        setSoundStatus('success');
        if (currentStep === 0) setCurrentStep(1);
      }, 1000);
    }
  };

  const handleMicTest = async () => {
    setMicStatus('running');
    
    // Check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('getUserMedia not supported');
      setMicStatus('error');
      return;
    }
    
    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout')), 10000);
    });
    
    try {
      // Race between getUserMedia and timeout
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({ audio: true }),
        timeoutPromise
      ]) as MediaStream;
      
      // Permission granted! Stop the stream and mark as success
      stream.getTracks().forEach(track => track.stop());
      
      setMicStatus('success');
      if (currentStep === 1) setCurrentStep(2);
    } catch (error: any) {
      console.error('Microphone test error:', error?.name, error?.message);
      
      if (error?.message === 'Timeout') {
        // Timeout - probably permission dialog not showing or dismissed
        alert(t('preExam.micTest.timeout'));
      }
      
      setMicStatus('error');
    }
  };

  const handleStartExam = async () => {
    if (!allReady) return;
    if (subscriptionBlocked || verificationRequired || emailVerificationBlocked) return;
    setSubscriptionError(null);
    const sectionTyped = section as Section;

    if (examId && sectionUsesStartExam(sectionTyped)) {
      setStartLoading(true);
      try {
        const data = await examService.startExam(examId);
        setStartLoading(false);
        const examPath = getExamPath(sectionTyped);
        const params = new URLSearchParams({ attemptId: data.attemptId, section });
        navigate(`${examPath}?${params.toString()}`);
      } catch (err: any) {
        setStartLoading(false);
        const status = err?.response?.status;
        const code = err?.response?.data?.code;
        const message = err?.response?.data?.message;
        if (code === 'EMAIL_VERIFICATION_REQUIRED') {
          setVerificationRequired(true);
          setSubscriptionError(null);
          return;
        }
        setSubscriptionError(
          status === 403
            ? message || 'This exam requires an active subscription.'
            : message || 'Failed to start exam. Please try again.',
        );
      }
      return;
    }

    const params = new URLSearchParams({ section });
    navigate(`${getExamPath(sectionTyped)}?${params.toString()}`);
  };

  const renderStatusBadge = (status: TestStatus) => {
    switch (status) {
      case 'success':
        return (
          <span className="inline-flex items-center text-sm text-success bg-success/10 px-3 py-1.5 rounded-full font-medium">
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {t('preExam.status.ready')}
          </span>
        );
      case 'running':
        return (
          <span className="inline-flex items-center text-sm text-primary bg-primary/10 px-3 py-1.5 rounded-full font-medium">
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            {t('preExam.status.testing')}
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center text-sm text-error bg-error/10 px-3 py-1.5 rounded-full font-medium">
            <AlertTriangle className="w-4 h-4 mr-1.5" />
            {t('preExam.status.issue')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full font-medium">
            {t('preExam.status.notTested')}
          </span>
        );
    }
  };

  const steps = needsMic 
    ? [t('preExam.steps.sound'), t('preExam.steps.microphone'), t('preExam.steps.instructions')] 
    : needsSound
    ? [t('preExam.steps.sound'), t('preExam.steps.instructions')]
    : [t('preExam.steps.instructions')];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Spacer for fixed header */}
      <div className="h-20" />

      <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl">
        {/* Back button */}
        <button 
          onClick={() => navigate('/exam-selection')}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('preExam.backToSelection')}
        </button>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
              <PlayCircle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('preExam.title')}</h1>
              <p className="text-gray-600">
                {t('preExam.subtitle')}
              </p>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-6">
            {steps.map((step, idx) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      idx < currentStep
                        ? 'bg-success text-white'
                        : idx === currentStep
                        ? 'bg-secondary text-white shadow-lg'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {idx < currentStep ? <CheckCircle2 className="w-5 h-5" /> : idx + 1}
                  </div>
                  <span className={`text-xs mt-1 ${idx === currentStep ? 'text-secondary font-medium' : 'text-gray-500'}`}>
                    {step}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-1 rounded mb-5 ${idx < currentStep ? 'bg-success' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Exam Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{t('preExam.examDetails')}</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-primary/5 p-4 rounded-xl">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{exam?.type === 'knm' ? t('knm.subjectLabel') : t('preExam.level')}</p>
              <p className="font-bold text-primary text-lg">{exam?.type === 'knm' ? t('knm.subject') : exam?.level || '—'}</p>
              {exam?.title && <p className="text-sm text-gray-500 truncate">{exam.type === 'knm' && exam.isTrial ? t('knm.trialTitle') : exam.title}</p>}
            </div>
            <div className="bg-secondary/10 p-4 rounded-xl">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t('examSelection.section')}</p>
              <p className="font-bold text-secondary text-lg">{section}</p>
            </div>
            <div className="bg-gray-100 p-4 rounded-xl">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t('examSelection.duration')}</p>
              <p className="font-bold text-gray-900 text-lg">{duration}</p>
            </div>
          </div>
        </div>

        {/* Free trial notice */}
        {exam?.isTrial && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-4">
            <Info className="w-5 h-5 flex-shrink-0 text-primary mt-0.5" />
            <div>
              <p className="mb-1 font-medium text-ink">{t('preExam.trialNotice.title')}</p>
              <p className="mb-2 text-sm text-slate-600">{t(exam?.type === 'knm' ? 'knm.trialDescription' : 'preExam.trialNotice.description')}</p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 font-medium text-primary hover:underline"
              >
                {t('preExam.viewPlans')}
              </Link>
            </div>
          </div>
        )}

        {/* Sound Test (not needed for Lezen) */}
        {needsSound && (
        <div className={`bg-white rounded-2xl shadow-lg p-6 mb-4 transition-all ${currentStep === 0 ? 'ring-2 ring-secondary' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                soundStatus === 'success' ? 'bg-success/10' : 'bg-indigo-50'
              }`}>
                {soundStatus === 'error' ? (
                  <VolumeX className="w-6 h-6 text-error" />
                ) : (
                  <Volume2 className={`w-6 h-6 ${soundStatus === 'success' ? 'text-success' : 'text-indigo-600'}`} />
                )}
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">{t('preExam.soundTest.title')}</h3>
                <p className="text-sm text-gray-600">
                  {t('preExam.soundTest.description')}
                </p>
              </div>
            </div>
            {renderStatusBadge(soundStatus)}
          </div>
          
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <p className="text-sm text-gray-600">
              {t('preExam.soundTest.instructions')}
            </p>
          </div>
          
          <Button
            variant={soundStatus === 'success' ? 'tertiary' : 'primary'}
            onClick={handleSoundTest}
            disabled={soundStatus === 'running'}
            icon={soundStatus === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
          >
            {soundStatus === 'success' ? t('preExam.soundTest.retest') : t('preExam.soundTest.button')}
          </Button>
        </div>
        )}

        {/* Microphone Test (only for Spreken) */}
        {needsMic && (
          <div className={`bg-white rounded-2xl shadow-lg p-6 mb-4 transition-all ${currentStep === 1 ? 'ring-2 ring-secondary' : ''}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  micStatus === 'success' ? 'bg-success/10' : micStatus === 'error' ? 'bg-error/10' : 'bg-green-50'
                }`}>
                  {micStatus === 'error' ? (
                    <MicOff className="w-6 h-6 text-error" />
                  ) : (
                    <Mic className={`w-6 h-6 ${micStatus === 'success' ? 'text-success' : 'text-green-600'}`} />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">{t('preExam.micTest.title')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('preExam.micTest.description')}
                  </p>
                </div>
              </div>
              {renderStatusBadge(micStatus)}
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600">
                {t('preExam.micTest.instructions')}
              </p>
            </div>
            
            {micStatus === 'error' && (
              <div className="bg-error/10 border border-error/20 rounded-xl p-4 mb-4">
                <p className="text-sm text-error font-medium">
                  ⚠️ {t('preExam.micTest.error')}
                </p>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant={micStatus === 'success' ? 'tertiary' : 'primary'}
                onClick={handleMicTest}
                disabled={micStatus === 'running' || soundStatus !== 'success'}
                icon={micStatus === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
              >
                {micStatus === 'success' ? t('preExam.micTest.retest') : t('preExam.micTest.button')}
              </Button>
              
              {micStatus !== 'success' && (
                <Button
                  variant="tertiary"
                  onClick={() => {
                    setMicStatus('success');
                    if (currentStep === 1) setCurrentStep(2);
                  }}
                  className="text-gray-500"
                >
                  {t('preExam.micTest.skip')}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Instructions and Rules */}
        <div className={`bg-white rounded-2xl shadow-lg p-6 mb-6 transition-all ${
          (needsMic && currentStep === 2) || (!needsMic && needsSound && currentStep === 1) || (!needsSound && currentStep === 0) ? 'ring-2 ring-secondary' : ''
        }`}>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-yellow-50 flex items-center justify-center">
              <Info className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">{t('preExam.instructions.title')}</h3>
              <p className="text-sm text-gray-600">{t('preExam.instructions.subtitle')}</p>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-5 mb-4">
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">1.</span>
                <span>{t('preExam.instructions.list.phone')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">2.</span>
                <span>{t('preExam.instructions.list.materials')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">3.</span>
                <span>{t('preExam.instructions.list.distractions')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">4.</span>
                <span>{t('preExam.instructions.list.readCarefully')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">5.</span>
                <span>{t('preExam.instructions.list.duration', { duration })}</span>
              </li>
            </ul>
          </div>
          
          <label className="flex items-start gap-3 p-4 bg-primary/5 rounded-xl cursor-pointer hover:bg-primary/10 transition-colors">
            <input
              type="checkbox"
              className="mt-0.5 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
              checked={acceptedRules}
              onChange={(e) => setAcceptedRules(e.target.checked)}
            />
            <span className="text-sm text-gray-700">
              {t('preExam.confirmation')}
            </span>
          </label>
        </div>

        {(verificationRequired || emailVerificationBlocked) && (
          <EmailVerificationPanel className="mb-6" />
        )}

        {subscriptionError && !verificationRequired && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="font-medium text-amber-800 mb-2">{subscriptionError}</p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-amber-800 font-medium hover:underline"
            >
              {t('preExam.viewPlans')}
            </Link>
          </div>
        )}

        {subscriptionBlocked && !verificationRequired && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 mb-1">
                {t('preExam.subscriptionRequired')}
              </p>
              <p className="text-sm text-amber-700 mb-2">
                {t('preExam.subscriptionRequiredHint')}
              </p>
              <Link
                to="/dashboard/subscription"
                className="inline-flex items-center gap-2 text-amber-800 font-medium hover:underline"
              >
                {t('preExam.viewSubscription')}
              </Link>
            </div>
          </div>
        )}

        {/* Final Action */}
        <div className="flex gap-4">
          <Button
            variant="tertiary"
            className="flex-1"
            onClick={() => navigate('/exam-selection')}
            disabled={startLoading}
          >
            {t('preExam.back')}
          </Button>
          <Button
            variant="primary"
            className={`flex-1 ${allReady ? 'bg-success hover:bg-success/90' : ''}`}
            disabled={!allReady || startLoading || subscriptionBlocked || verificationRequired || emailVerificationBlocked}
            loading={startLoading}
            onClick={handleStartExam}
          >
            {t('preExam.startExam')}
          </Button>
        </div>

        {!allReady && (
          <p className="text-center text-sm text-gray-500 mt-4">
            {soundStatus !== 'success' && `⚠️ ${t('preExam.warnings.soundFirst')} `}
            {needsMic && micStatus !== 'success' && `⚠️ ${t('preExam.warnings.micFirst')} `}
            {!acceptedRules && `⚠️ ${t('preExam.warnings.acceptFirst')}`}
          </p>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default PreExamSetupPage;
